// ============================================================================
// DUPLICATES MERGE PANEL
// Side panel for batch-reviewing and merging semantically duplicate graph
// nodes. Triggered by facilitator action; shows a diff-style card per group
// with an AI-suggested merged name + description that can be edited inline
// before committing.
// ============================================================================

import React, { useState, useCallback } from 'react';
import { X, GitMerge, SkipForward, Loader2, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { customerResearchService, GraphNode } from '../../services/sessionTasksService';
import { getNodeConfig } from './customerResearchGraphHelpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DuplicateGroup {
  nodes: GraphNode[];
  suggestedName: string;
  suggestedDescription: string;
}

interface DuplicatesMergePanelProps {
  projectId: string;
  sessionId: string;
  onClose: () => void;
  onMergeComplete: () => void;
}

type GroupDecision = 'pending' | 'approved' | 'skipped';

interface GroupState {
  group: DuplicateGroup;
  decision: GroupDecision;
  mergedName: string;
  mergedDescription: string;
  keepNodeId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const NodeCard: React.FC<{ node: GraphNode; isKeep: boolean; onSelectKeep: () => void; canSelectKeep: boolean }> = ({
  node, isKeep, onSelectKeep, canSelectKeep
}) => {
  const config = getNodeConfig(node.type);
  const label = node.name || node.text || node.story || node.id;
  return (
    <div
      className={`flex-1 min-w-0 rounded-lg border p-3 transition-all ${
        isKeep
          ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30'
          : 'border-slate-200 bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: config.color }} />
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{node.type}</span>
        {isKeep && (
          <span className="ml-auto text-[9px] font-bold text-primary uppercase tracking-wide">Keep</span>
        )}
      </div>
      <p className="text-xs font-semibold text-slate-800 break-words line-clamp-3">{label}</p>
      {canSelectKeep && !isKeep && (
        <button
          onClick={onSelectKeep}
          className="mt-2 text-[10px] text-slate-400 hover:text-primary transition-colors underline"
        >
          Use this as survivor
        </button>
      )}
    </div>
  );
};

const getCardStateClass = (decision: GroupDecision): string => {
  if (decision === 'approved') return 'border-primary/40 bg-primary/3';
  if (decision === 'skipped') return 'border-slate-200 opacity-50';
  return 'border-slate-200 bg-white';
};

interface DuplicateGroupCardProps {
  state: GroupState;
  idx: number;
  onSetDecision: (index: number, decision: GroupDecision) => void;
  onSetMergedName: (index: number, value: string) => void;
  onSetMergedDescription: (index: number, value: string) => void;
  onSelectKeep: (index: number, nodeId: string) => () => void;
}

const DuplicateGroupCard: React.FC<DuplicateGroupCardProps> = ({
  state, idx, onSetDecision, onSetMergedName, onSetMergedDescription, onSelectKeep
}) => {
  const cardStateClass = getCardStateClass(state.decision);

  return (
    <div
      className={`rounded-xl border transition-all ${cardStateClass}`}
    >
      {/* Node pair */}
      <div className="flex items-stretch gap-2 p-3">
        {state.group.nodes.slice(0, 2).map((node, ni) => (
          <React.Fragment key={node.id}>
            <NodeCard
              node={node}
              isKeep={node.id === state.keepNodeId}
              onSelectKeep={onSelectKeep(idx, node.id)}
              canSelectKeep={state.group.nodes.length === 2}
            />
            {ni < state.group.nodes.slice(0, 2).length - 1 && (
              <div className="flex items-center shrink-0">
                <ChevronRight size={14} className="text-slate-300" />
              </div>
            )}
          </React.Fragment>
        ))}
        {state.group.nodes.length > 2 && (
          <div className="flex items-center shrink-0">
            <span className="text-[10px] text-slate-400">+{state.group.nodes.length - 2} more</span>
          </div>
        )}
      </div>

      {/* Merged result (editable) */}
      <div className="px-3 pb-3 space-y-2 border-t border-slate-100 pt-3">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Merged result</p>
        <input
          type="text"
          value={state.mergedName}
          onChange={e => onSetMergedName(idx, e.target.value)}
          placeholder="Merged name"
          className="w-full text-xs font-semibold border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
        />
        <textarea
          value={state.mergedDescription}
          onChange={e => onSetMergedDescription(idx, e.target.value)}
          placeholder="Merged description"
          rows={2}
          className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 px-3 pb-3">
        <button
          onClick={() => onSetDecision(idx, state.decision === 'approved' ? 'pending' : 'approved')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            state.decision === 'approved'
              ? 'bg-primary text-white'
              : 'bg-primary/10 text-primary hover:bg-primary/20'
          }`}
        >
          <GitMerge size={12} />
          {state.decision === 'approved' ? 'Approved' : 'Approve merge'}
        </button>
        <button
          onClick={() => onSetDecision(idx, state.decision === 'skipped' ? 'pending' : 'skipped')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            state.decision === 'skipped'
              ? 'bg-slate-200 text-slate-600'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          <SkipForward size={12} />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DuplicatesMergePanel: React.FC<DuplicatesMergePanelProps> = ({
  projectId,
  sessionId,
  onClose,
  onMergeComplete
}) => {
  const [phase, setPhase] = useState<'idle' | 'detecting' | 'review' | 'applying' | 'done'>('idle');
  const [groupStates, setGroupStates] = useState<GroupState[]>([]);
  const [applyProgress, setApplyProgress] = useState(0);
  const [applyErrors, setApplyErrors] = useState<string[]>([]);
  const [detectError, setDetectError] = useState<string | null>(null);

  const handleDetect = useCallback(async () => {
    setPhase('detecting');
    setDetectError(null);
    try {
      const result = await customerResearchService.findDuplicates(projectId, sessionId);
      const states: GroupState[] = result.groups.map(g => ({
        group: g,
        decision: 'pending',
        mergedName: g.suggestedName,
        mergedDescription: g.suggestedDescription,
        keepNodeId: g.nodes[0].id
      }));
      setGroupStates(states);
      setPhase('review');
    } catch (err: any) {
      setDetectError(err?.response?.data?.error || err.message || 'Detection failed');
      setPhase('idle');
    }
  }, [projectId, sessionId]);

  const setDecision = useCallback((index: number, decision: GroupDecision) => {
    setGroupStates(prev => prev.map((s, i) => i === index ? { ...s, decision } : s));
  }, []);

  const setMergedName = useCallback((index: number, value: string) => {
    setGroupStates(prev => prev.map((s, i) => i === index ? { ...s, mergedName: value } : s));
  }, []);

  const setMergedDescription = useCallback((index: number, value: string) => {
    setGroupStates(prev => prev.map((s, i) => i === index ? { ...s, mergedDescription: value } : s));
  }, []);

  const setKeepNode = useCallback((index: number, nodeId: string) => {
    setGroupStates(prev => prev.map((s, i) => i === index ? { ...s, keepNodeId: nodeId } : s));
  }, []);

  const skipAll = () => setGroupStates(prev => prev.map(s => ({ ...s, decision: 'skipped' as const })));
  const approveAll = () => setGroupStates(prev => prev.map(s => ({ ...s, decision: 'approved' as const })));
  const makeSelectKeepHandler = (index: number, nodeId: string) => () => setKeepNode(index, nodeId);

  const approvedCount = groupStates.filter(s => s.decision === 'approved').length;

  const handleApply = useCallback(async () => {
    const toMerge = groupStates.filter(s => s.decision === 'approved');
    setPhase('applying');
    setApplyProgress(0);
    setApplyErrors([]);

    const errors: string[] = [];
    for (let i = 0; i < toMerge.length; i++) {
      const { group, keepNodeId, mergedName, mergedDescription } = toMerge[i];
      const mergeNodeIds = group.nodes.map(n => n.id).filter(id => id !== keepNodeId);
      for (const mergeNodeId of mergeNodeIds) {
        try {
          await customerResearchService.mergeNodes(
            projectId,
            keepNodeId,
            mergeNodeId,
            mergedName.trim() || undefined,
            mergedDescription.trim() || undefined
          );
        } catch (err: any) {
          errors.push(`Failed to merge "${group.nodes.find(n => n.id === mergeNodeId)?.name || mergeNodeId}": ${err?.response?.data?.error || err.message}`);
        }
      }
      setApplyProgress(i + 1);
    }

    setApplyErrors(errors);
    setPhase('done');
    onMergeComplete();
  }, [groupStates, projectId, onMergeComplete]);

  // ---------------------------------------------------------------------------
  // Phases
  // ---------------------------------------------------------------------------

  const renderIdle = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-4 px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
        <GitMerge size={22} className="text-primary" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-1">Find duplicate nodes</p>
        <p className="text-xs text-slate-500">
          AI scans all nodes in this session for semantic duplicates and suggests merged names and descriptions.
        </p>
      </div>
      {detectError && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 w-full">
          <AlertTriangle size={13} className="shrink-0" />
          {detectError}
        </div>
      )}
      <button
        onClick={handleDetect}
        className="px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors"
      >
        Detect duplicates
      </button>
    </div>
  );

  const renderDetecting = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center">
      <Loader2 size={28} className="animate-spin text-primary" />
      <p className="text-sm text-slate-600">Analysing session nodes…</p>
    </div>
  );

  const renderReview = () => {
    if (groupStates.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center px-6">
          <CheckCircle2 size={28} className="text-green-500" />
          <p className="text-sm font-semibold text-slate-800">No duplicates found</p>
          <p className="text-xs text-slate-500">All nodes in this session appear to be unique.</p>
          <button onClick={onClose} className="mt-2 px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            Close
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{groupStates.length}</span> duplicate group{groupStates.length !== 1 ? 's' : ''} found
          </p>
          <div className="flex gap-2">
            <button
              onClick={skipAll}
              className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
            >
              Skip all
            </button>
            <button
              onClick={approveAll}
              className="text-[10px] text-primary hover:text-primary-dark transition-colors"
            >
              Approve all
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {groupStates.map((state, idx) => (
            <DuplicateGroupCard
              key={state.group.nodes.map(n => n.id).join('-')}
              state={state}
              idx={idx}
              onSetDecision={setDecision}
              onSetMergedName={setMergedName}
              onSetMergedDescription={setMergedDescription}
              onSelectKeep={makeSelectKeepHandler}
            />
          ))}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-3">
          <span className="text-xs text-slate-500 flex-1">
            {approvedCount} of {groupStates.length} approved
          </span>
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={approvedCount === 0}
            className="px-4 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            <GitMerge size={12} />
            Apply {approvedCount} merge{approvedCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    );
  };

  const renderApplying = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center">
      <Loader2 size={28} className="animate-spin text-primary" />
      <p className="text-sm text-slate-600">
        Applying merge {applyProgress} of {groupStates.filter(s => s.decision === 'approved').length}…
      </p>
    </div>
  );

  const renderDone = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-4 px-6 text-center">
      <CheckCircle2 size={32} className="text-green-500" />
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-1">
          {approvedCount} merge{approvedCount !== 1 ? 's' : ''} applied
        </p>
        <p className="text-xs text-slate-500">The graph has been updated.</p>
      </div>
      {applyErrors.length > 0 && (
        <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3 text-left space-y-1">
          <p className="text-xs font-semibold text-red-700">Some merges failed:</p>
          {applyErrors.map((e) => (
            <p key={e} className="text-[10px] text-red-600">{e}</p>
          ))}
        </div>
      )}
      <button
        onClick={onClose}
        className="px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors"
      >
        Done
      </button>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="w-80 flex-shrink-0 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden" style={{ maxHeight: '650px' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <GitMerge size={15} className="text-primary" />
          <span className="text-sm font-semibold text-slate-800">Merge duplicates</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
        >
          <X size={15} />
        </button>
      </div>

      {phase === 'idle' && renderIdle()}
      {phase === 'detecting' && renderDetecting()}
      {phase === 'review' && renderReview()}
      {phase === 'applying' && renderApplying()}
      {phase === 'done' && renderDone()}
    </div>
  );
};
